import { Box, Icon, Tooltip, Typography } from '@mui/material';
import MoedaText from '../ui/inputs/moedaText';
import { differenceBy, find, map } from 'lodash';
import InteiroText from '../ui/inputs/inteiroText';
import PosicaoAtivo from '../../../models/posicaoAtivo';
import TabelaBase from './tabelaBase';
import Ticker from '../ui/ticker';
import CheckpointAtivo from '../../../models/checkpointAtivo';
import { IconAlertTriangle, IconCheck } from '@tabler/icons-react';

type Props = {
  ativos: PosicaoAtivo[] | undefined;
  checkpoints: CheckpointAtivo[] | undefined;
  loading?: boolean;
};

export default function TabelaCheckpoint({
  ativos,
  checkpoints,
  loading,
}: Props) {
  const header: {
    titulo: string;
    key: string;
    ordem: number;
    align: 'left' | 'center' | 'right';
  }[] = [
    { titulo: 'Ativo', key: 'ativo', ordem: 1, align: 'center' },
    { titulo: 'C/V', key: 'cv', ordem: 2, align: 'center' },
    { titulo: 'Mercado', key: 'mercado', ordem: 3, align: 'center' },
    { titulo: 'Prazo', key: 'prazo', ordem: 4, align: 'right' },
    { titulo: 'PM', key: 'precoMedio', ordem: 5, align: 'right' },
    { titulo: 'Qtd.', key: 'quantidade', ordem: 6, align: 'right' },
    { titulo: 'Qtd. B3', key: 'quantidadeB3', ordem: 7, align: 'right' },
    {
      titulo: 'Valor Apl.',
      key: 'valorTotal',
      ordem: 8,
      align: 'right',
    },
  ];

  const rows = [
    ...map(ativos, (ativo) => {
      const check = find(
        checkpoints,
        (c) => c.codigoAtivo === ativo.codigoAtivo,
      );
      return {
        mercado: celulaMercado(ativo),
        cv: celulaCV(ativo),
        ativo: celulaAtivo(ativo),
        prazo: celulaPrazo(ativo),
        quantidade: celulaQuantidade(ativo),
        quantidadeB3: celulaQuantidadeB3(ativo, check),
        precoMedio: celulaPrecoMedio(ativo),
        valorTotal: celulaValorTotal(ativo),
      };
    }),
    ...map(differenceBy(checkpoints, ativos || [], 'codigoAtivo'), (check) => ({
      ativo: celulaAtivo(check),
      quantidade: celulaQuantidade(undefined),
      quantidadeB3: celulaQuantidadeB3(undefined, check),
    })),
  ];

  return <TabelaBase header={header} rows={rows} loading={loading} />;
  function celulaMercado(ativo: PosicaoAtivo): JSX.Element {
    return celulaTexto(ativo.mercado);
  }

  function celulaCV(ativo: PosicaoAtivo): JSX.Element {
    return celulaTexto(ativo.tipoOperacao);
  }

  function celulaAtivo(ativo: PosicaoAtivo | CheckpointAtivo): JSX.Element {
    return <Ticker codigoAtivo={ativo.codigoAtivo} />;
  }

  function celulaPrazo(ativo: PosicaoAtivo): JSX.Element {
    return (
      <Typography variant="dataMono">
        {ativo.prazo?.format('DD/MM/YYYY')}
      </Typography>
    );
  }

  function celulaQuantidade(ativo: PosicaoAtivo | undefined): JSX.Element {
    return (
      <Typography variant="dataMono">
        <InteiroText
          value={ativo?.quantidade ?? 0}
          displayType={'text'}
        ></InteiroText>
      </Typography>
    );
  }

  function celulaQuantidadeB3(
    ativo: PosicaoAtivo | undefined,
    check: CheckpointAtivo | undefined,
  ): JSX.Element {
    return (
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
      >
        {ativo?.quantidade !== check?.quantidade ? (
          <Tooltip
            title={`Você tem ${ativo?.quantidade ?? 0}, mas o extrato mostrou ${
              check?.quantidade ?? 0
            }`}
          >
            <Icon color="warning" fontSize="inherit">
              <IconAlertTriangle size={14} />
            </Icon>
          </Tooltip>
        ) : (
          <Tooltip title="Quantidades coincidem">
            <Icon color="success" fontSize="inherit">
              <IconCheck size={14} />
            </Icon>
          </Tooltip>
        )}
        <Typography variant="dataMono">
          <InteiroText
            value={check?.quantidade ?? 0}
            displayType={'text'}
          ></InteiroText>
        </Typography>
      </Box>
    );
  }

  function celulaPrecoMedio(ativo: PosicaoAtivo): JSX.Element {
    return (
      <Typography variant="dataMono">
        <MoedaText displayType={'text'} value={ativo.precoMedio}></MoedaText>
      </Typography>
    );
  }

  function celulaValorTotal(ativo: PosicaoAtivo): JSX.Element {
    return (
      <Typography variant="dataMono">
        <MoedaText displayType={'text'} value={ativo.valorTotal}></MoedaText>
      </Typography>
    );
  }

  function celulaTexto(texto?: string): JSX.Element {
    return <Typography>{texto}</Typography>;
  }
}
